import { useCallback, useEffect, useRef, useState } from 'react';

import type { NavItemConfigInternal } from '../../../types';

interface ExpandOpenState {
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	onExpandFlyoutToggle: (isExpandedOrOpen: boolean) => void;
}

export function useExpandOpenState(item: NavItemConfigInternal, peek: boolean): ExpandOpenState {
	// We need to manage the state of expands and flyouts open state here so that we can collapse them during drag. But
	// the consuming app may also want to control this state. So, we will track the open state here and also listen for
	// the corresponding props in a useEffect so we can update our internal state if the app is controlling it.
	const { type } = item;

	const isExpandable = ['expand', 'flyout', 'object-list-flyout'].includes(type);
	const isExpandedFromProps = type === 'expand' ? item.isExpanded : undefined;
	const isOpenFromProps =
		type === 'flyout' || type === 'object-list-flyout' ? item.isOpen : undefined;
	const isExpandedOrOpenFromProps =
		typeof isExpandedFromProps === 'boolean' ? isExpandedFromProps : isOpenFromProps;
	const isDefaultExpandedOrOpenFromProps =
		(type === 'expand' && item.isDefaultExpanded) ||
		((type === 'flyout' || type === 'object-list-flyout') && item.isDefaultOpen);
	const [isExpandOrFlyoutOpen, setIsExpandOrFlyoutOpen] = useState(
		// Prioritize controlled props if available, otherwise use default expanded/open state or peek state
		typeof isExpandedOrOpenFromProps === 'boolean'
			? isExpandedOrOpenFromProps
			: isDefaultExpandedOrOpenFromProps || peek,
	);
	useEffect(() => {
		if (!isExpandable || typeof isExpandedOrOpenFromProps !== 'boolean') {
			return;
		}
		if (isExpandedOrOpenFromProps !== isExpandOrFlyoutOpen) {
			setIsExpandOrFlyoutOpen(isExpandedOrOpenFromProps);
		}
	}, [isExpandable, isExpandOrFlyoutOpen, isExpandedOrOpenFromProps]);

	const maybeOnExpansionToggle = type === 'expand' ? item.onExpansionToggle : undefined;
	const maybeOnOpenChange =
		type === 'flyout' || type === 'object-list-flyout' ? item.onOpenChange : undefined;
	const onExpandFlyoutToggle = useCallback(
		(isExpandedOrOpen: boolean) => {
			if (type === 'expand') {
				setIsExpandOrFlyoutOpen(isExpandedOrOpen);
				maybeOnExpansionToggle?.(isExpandedOrOpen);
			} else if (type === 'flyout' || type === 'object-list-flyout') {
				setIsExpandOrFlyoutOpen(isExpandedOrOpen);
				maybeOnOpenChange?.(isExpandedOrOpen);
			}
		},
		[type, maybeOnExpansionToggle, maybeOnOpenChange],
	);

	// If an item is being peeked and it is controlled, open/expand it by calling the toggle function
	const prevPeek = useRef<boolean | null>(null);
	useEffect(() => {
		if (peek && peek !== prevPeek.current && isExpandable && !isExpandOrFlyoutOpen) {
			onExpandFlyoutToggle(true);
		}
		prevPeek.current = peek;
	}, [peek, prevPeek, isExpandable, isExpandOrFlyoutOpen, onExpandFlyoutToggle]);

	return {
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isExpandable,
	};
}
